import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import api from './api';

const darkStyles = {
  control: (provided) => ({ ...provided, backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', boxShadow: 'none' }),
  menu: (provided) => ({ ...provided, backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', zIndex: 9999 }),
  option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? 'var(--bg-tertiary)' : 'var(--bg-primary)', color: 'var(--text-main)', cursor: 'pointer' }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-main)' }),
  input: (provided) => ({ ...provided, color: 'var(--text-main)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-muted)' }),
  clearIndicator: (provided) => ({ ...provided, color: 'var(--text-muted)' }),
  dropdownIndicator: (provided) => ({ ...provided, color: 'var(--text-muted)' }),
};

// Aydınlık tema için stiller (Bootstrap'in varsayılan form renkleriyle uyumlu)
const lightStyles = {
  control: (provided) => ({ ...provided, backgroundColor: '#f8f9fa', borderColor: '#ced4da' }),
  menu: (provided) => ({ ...provided, backgroundColor: 'white' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#e9ecef' : 'white',
    color: '#212529',
  }),
  singleValue: (provided) => ({ ...provided, color: '#212529' }),
  input: (provided) => ({ ...provided, color: '#212529' }),
};

const HAFTANIN_GUNLERI = [
  { value: 'Pazartesi', label: 'Pazartesi' },
  { value: 'Salı', label: 'Salı' },
  { value: 'Çarşamba', label: 'Çarşamba' },
  { value: 'Perşembe', label: 'Perşembe' },
  { value: 'Cuma', label: 'Cuma' },
  { value: 'Cumartesi', label: 'Cumartesi' },
  { value: 'Pazar', label: 'Pazar' }
];

function OzelOgunFormu({ danisanId, randevuId, onOgunEklendi, onIptal, sabitGun, sabitOgun }) {
  const [seciliKategori, setSeciliKategori] = useState(sabitOgun ? { value: sabitOgun, label: sabitOgun } : null);
  const [seciliGun, setSeciliGun] = useState(sabitGun ? { value: sabitGun, label: sabitGun } : null);
  const [icerikler, setIcerikler] = useState([{ besin: null, miktar: '', olcu_birimi: null }]);
  
  const [kategoriListesi, setKategoriListesi] = useState([]);
  const [besinListesi, setBesinListesi] = useState([]);
  const [olcuBirimiListesi, setOlcuBirimiListesi] = useState([]);

  const currentTheme = localStorage.getItem('theme') || 'light';
  const selectStyles = currentTheme === 'dark' ? darkStyles : lightStyles

  const kutuphaneleriGetir = useCallback(() => {
    api.get('/diyetisyen/kategoriler').then(res => setKategoriListesi(res.data.map(k => ({ value: k.id, label: k.ad })) || []));
    api.get('/diyetisyen/besinler').then(res => setBesinListesi(res.data.map(b => ({ value: b.id, label: b.ad })) || []));
    api.get('/diyetisyen/olcu-birimleri').then(res => setOlcuBirimiListesi(res.data.map(o => ({ value: o.id, label: o.ad })) || []));
  }, []);

  useEffect(() => {
    kutuphaneleriGetir();
  }, [kutuphaneleriGetir]);

  const handleIcerikDegistir = (index, name, value) => {
    const yeniIcerikler = [...icerikler];
    yeniIcerikler[index][name] = value;
    setIcerikler(yeniIcerikler);
  };
  
  const handleIcerikEkle = () => setIcerikler([...icerikler, { besin: null, miktar: '', olcu_birimi: null }]);
  
  const handleIcerikSil = (index) => {
    const yeniIcerikler = [...icerikler];
    yeniIcerikler.splice(index, 1);
    setIcerikler(yeniIcerikler);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!seciliKategori) return alert('Lütfen bir öğün kategorisi seçin veya oluşturun.');
    const gecerliIcerikler = icerikler.filter(ic => ic.besin && ic.miktar && ic.olcu_birimi);
    if (gecerliIcerikler.length === 0) return alert('En az bir geçerli içerik satırını tam olarak doldurun.');

    // Yardımcı fonksiyon: Backend'deki "get or create" mantığına güvenir.
    const getOrCreateId = async (item, endpoint) => {
      // Eğer listeden seçilmiş bir obje ise, direkt ID'sini döndür
      if (item.value && !item.__isNew__) {
        return item.value;
      }
      // Eğer yeni bir metin girilmişse, adı gönder. Backend bulamazsa oluşturacak.
      const ad = item.label || item.value || item;
      const res = await api.post(`/diyetisyen${endpoint}`, { ad });
      return res.data.id || res.data.besin_id;
    };

    try {
      // Seçilen kategorinin adını al (yeni veya mevcut)
      // YENİ EKLENDİ: Gün ile Öğün birleştiriliyor (Salı::Öğle formunda)
      const gunStr = seciliGun ? seciliGun.value : 'Her Gün';
      const ogunAdi = `${gunStr}::${seciliKategori.label}`;

      const islenmisIcerikler = await Promise.all(
        gecerliIcerikler.map(async (icerik) => {
          const besin_id = await getOrCreateId(icerik.besin, '/besinler');
          const olcu_birimi_id = await getOrCreateId(icerik.olcu_birimi, '/olcu-birimleri');
          if (!besin_id || !olcu_birimi_id) throw new Error("İçerik ID'leri alınamadı.");
          return { miktar: icerik.miktar, besin_id, olcu_birimi_id };
        })
      );
      
      const payload = {
        randevu_id: randevuId,
        ogun_adi: ogunAdi,
        icerikler: islenmisIcerikler
      };
      
      await api.post(`/diyetisyen/danisanlar/${danisanId}/yemekgunlugu`, payload);
      
      onOgunEklendi(); // Ana bileşeni yenile
      kutuphaneleriGetir(); // Yeni eklenmiş olabilecek öğeler için kütüphaneleri de yenile

      // Formu temizle
      setSeciliKategori(null);
      setIcerikler([{ besin: null, miktar: '', olcu_birimi: null }]);

    } catch (error) {
      alert(error.response?.data?.mesaj || 'Özel öğün eklenemedi.');
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="mt-3 border-top pt-3">
      {(!sabitGun || !sabitOgun) && (
        <Row className="mb-3">
          {!sabitGun && (
            <Col md={5}>
              <Form.Group>
                <Form.Label className="fw-bold">Gün Seçimi</Form.Label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  placeholder="Gün Seç..."
                  options={HAFTANIN_GUNLERI}
                  value={seciliGun}
                  onChange={(newValue) => setSeciliGun(newValue)}
                />
              </Form.Group>
            </Col>
          )}
          {!sabitOgun && (
            <Col md={sabitGun ? 12 : 7}>
              <Form.Group>
                <Form.Label className="fw-bold">Öğün Kategorisi (örn: Kahvaltı)</Form.Label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  styles={selectStyles}
                  placeholder="Kategori Seç veya Yaz..."
                  options={kategoriListesi}
                  value={seciliKategori}
                  onChange={(newValue) => setSeciliKategori(newValue)}
                  formatCreateLabel={userInput => `"${userInput}" oluştur`}
                  noOptionsMessage={() => "Seçenek yok"}
                />
              </Form.Group>
            </Col>
          )}
        </Row>
      )}
      
      {icerikler.map((icerik, index) => (
        <Row key={index} className="mb-2 align-items-center">
          <Col xs={3}>
            <Form.Control type="number" placeholder="Miktar" value={icerik.miktar} onChange={(e) => handleIcerikDegistir(index, 'miktar', e.target.value)}
              required 
              className={currentTheme === 'dark' ? 'glass-input' : ''} />
          </Col>
          <Col xs={4}>
            <CreatableSelect
              styles={selectStyles}
              placeholder="Ölçü"
              isClearable
              isSearchable
              options={olcuBirimiListesi}
              value={icerik.olcu_birimi}
              onChange={(newValue) => handleIcerikDegistir(index, 'olcu_birimi', newValue)}
              formatCreateLabel={userInput => `"${userInput}" oluştur`}
              noOptionsMessage={() => "Seçenek yok"}
            />
          </Col>
          <Col xs={4}>
            <CreatableSelect
              styles={selectStyles}
              placeholder="Besin"
              isClearable
              isSearchable
              options={besinListesi}
              value={icerik.besin}
              onChange={(newValue) => handleIcerikDegistir(index, 'besin', newValue)}
              formatCreateLabel={userInput => `"${userInput}" oluştur`}
              noOptionsMessage={() => "Seçenek yok"}
            />
          </Col>
          <Col xs={1} className="text-end">
            <Button variant="outline-danger" size="sm" onClick={() => handleIcerikSil(index)}>X</Button>
          </Col>
        </Row>
      ))}

      <div className="d-flex justify-content-between mt-2">
        <Button variant="outline-secondary" size="sm" onClick={handleIcerikEkle}>+ İçerik Ekle</Button>
        <div className="d-flex">
          <Button variant="secondary" onClick={onIptal} className="me-2">İptal</Button>
          <Button type="submit" variant="primary">Öğünü Kaydet</Button>
        </div>
      </div>
    </Form>
  );
}

export default OzelOgunFormu;